import React from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import KpiaResultsModal from '../KpiaResultsModal/KpiaResultsModal';
import ModalArticleInfo from '../../../molecules/ModalArticleInfo/ModalArticleInfoBrandAware';
import ArticleModalSectionTabs from './ArticleModalSectionTabs';

const ArticleModalKpiaFace = ({
  article,
  initialSection,
  loading,
  open,
  onClose,
  onExit,
  onRecalculate
}) => {
  const [section, setSection] = React.useState('smartscore');

  React.useEffect(() => {
    if (open) setSection(initialSection);
  }, [initialSection, open]);

  if (loading) return null;

  return (
    <Box
      aria-hidden={!open}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: open ? 2 : 1,
        bgcolor: 'background.paper',
        display: { xs: open ? 'flex' : 'none', md: 'flex' },
        flexDirection: 'column',
        overflow: 'hidden',
        pointerEvents: open ? 'auto' : 'none',
        opacity: { xs: 1, md: open ? 1 : 0 },
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: { xs: 'none', md: open ? 'rotateY(0deg)' : 'rotateY(180deg)' },
        transformOrigin: 'center center',
        transition: {
          xs: 'none',
          md: 'transform 560ms cubic-bezier(0.22, 1, 0.36, 1), opacity 1ms linear 230ms'
        },
        animation: {
          xs: 'kpiaPanelIn 260ms cubic-bezier(0.22, 1, 0.36, 1)',
          md: 'none'
        },
        '@keyframes kpiaPanelIn': {
          from: { opacity: 0, transform: 'translateX(22px)' },
          to: { opacity: 1, transform: 'translateX(0)' }
        }
      }}
    >
      <Box sx={{ px: 1.5, pt: 1, flexShrink: 0 }}>
        <Box sx={{ display: { xs: 'block', md: 'none' }, minHeight: 40, mb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <ButtonBase
            onClick={onExit}
            aria-label="Voltar para a lista de publicações"
            sx={{ minHeight: 40, px: 0.5, pr: 1.25, gap: 0.75, borderRadius: 1.5, color: 'text.secondary' }}
          >
            <ArrowBackIcon sx={{ fontSize: 19 }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 750 }}>Voltar</Typography>
          </ButtonBase>
        </Box>
        <ArticleModalSectionTabs
          active={section}
          onPerformance={() => setSection('performance')}
          onPublication={onClose}
          onSmartScore={() => setSection('smartscore')}
        />
      </Box>
      {section === 'smartscore' ? (
        <KpiaResultsModal
          embedded
          onClose={onClose}
          article={article}
          onRecalculate={onRecalculate}
        />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, pt: 0.5, pb: 4 }}>
          <ModalArticleInfo
            article={article}
            hideQualitativeButton
            sx={{ my: 0, p: 0, border: 0, bgcolor: 'transparent' }}
          />
        </Box>
      )}
    </Box>
  );
};

ArticleModalKpiaFace.propTypes = {
  article: PropTypes.shape({
    _id: PropTypes.string
  }).isRequired,
  initialSection: PropTypes.oneOf(['smartscore', 'performance']).isRequired,
  loading: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
  onRecalculate: PropTypes.func.isRequired
};

export default ArticleModalKpiaFace;
